import React from 'react';
import { Link } from 'react-router-dom';
import ArticleLayout from '../components/ArticleLayout';
import { SEO_PAGES } from '../lib/site';
import { getEvidenceById } from '../lib/evidence';
import FichaEmBranco from '../components/FichaEmBranco';
import GuiaBanner from '../components/GuiaBanner';

const page = SEO_PAGES.find((p) => p.path === '/mini-mental-pdf');
const brucki = getEvidenceById('brucki-2003');
const SCIELO_PDF = 'https://www.scielo.br/j/anp/a/YgRksxZVZ4b9j3gS4gw97NN/?format=pdf&lang=pt';
const DOI_URL = `https://doi.org/${brucki.primarySource.value}`;

const ANSWER =
  'Para quem procura “Mini Mental PDF”, a referência brasileira de Brucki et al. (2003) pode ser acessada no artigo original publicado em Arq Neuropsiquiatr e disponível no SciELO. O MiniMental.com.br não republica nem redesenha o formulário completo do MMSE: artigo em acesso aberto e licença do instrumento são questões diferentes.';

const RELATED = [
  { to: '/#calculadora', label: 'Usar Mini Mental online', note: 'some a pontuação sem enviar dados do paciente.' },
  { to: '/mini-exame-do-estado-mental', label: 'Mini Exame do Estado Mental', note: 'entenda finalidade, domínios e limites do MEEM.' },
  { to: '/escolaridade', label: 'Tabela por escolaridade', note: 'veja as medianas descritas por Brucki et al. (2003).' },
  { to: '/interpretacao-dos-resultados', label: 'Pontuação e interpretação', note: 'diferencie escore, referência e resultado alterado.' },
  { to: '/formulario-e-direitos-de-uso', label: 'Formulário e direitos de uso', note: 'consulte a política editorial sobre obtenção e reprodução do instrumento.' }
];

const REFERENCES = [`${brucki.citation} DOI: ${brucki.primarySource.value}.`];

export default function MiniMentalPdf() {
  const medians = brucki.results.educationMedians;

  return (
    <ArticleLayout page={page} answer={ANSWER} related={RELATED} references={REFERENCES}>
      <section aria-labelledby="ficha-registro">
        <h2 id="ficha-registro" className="text-2xl mb-5">Ficha de registro do Mini Mental para imprimir (em branco)</h2>
        <p className="text-[var(--text-muted)] mb-5">
          Uma folha A4 para preencher durante a aplicação: identificação, escolaridade, pontuação por bloco do protocolo brasileiro, condições da aplicação e observações. É uma ficha de registro própria do site: não reproduz o formulário do MMSE. Clique e escolha “Salvar como PDF” ou imprima direto.
        </p>
        <FichaEmBranco />
        <GuiaBanner placement="ficha" className="mt-5" />
      </section>

      <section aria-labelledby="pdf-original">
        <h2 id="pdf-original" className="text-2xl mb-5">PDF da referência brasileira de Brucki et al. (2003)</h2>
        <p className="text-[var(--text-muted)] mb-5">
          O artigo <strong>“Sugestões para o uso do Mini-Exame do Estado Mental no Brasil”</strong>, de Brucki, Nitrini, Caramelli, Bertolucci e Okamoto, é uma referência brasileira central para o MEEM e está disponível no SciELO.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <a href={SCIELO_PDF} target="_blank" rel="noreferrer" className="btn-primary text-center">
            Acessar PDF original no SciELO
          </a>
          <a href={DOI_URL} target="_blank" rel="noreferrer" className="flex items-center justify-center px-6 py-3 rounded-xl border border-[var(--border)] font-bold text-[var(--primary)] hover:border-[var(--accent)] transition-colors">
            Abrir registro pelo DOI
          </a>
        </div>
      </section>

      <section aria-labelledby="artigo-versus-formulario">
        <h2 id="artigo-versus-formulario" className="text-2xl mb-5">O PDF do artigo é o mesmo que um formulário livre para redistribuição?</h2>
        <p className="text-[var(--text-muted)] mb-4"><strong>Não é uma conclusão automática.</strong></p>
        <p className="text-[var(--text-muted)] mb-4">
          O fato de um artigo científico estar acessível gratuitamente não significa, por si só, que todo instrumento, formulário ou material reproduzido nele possa ser redesenhado, extraído e redistribuído comercialmente sem observar direitos e licenças próprios.
        </p>
        <p className="text-[var(--text-muted)]">
          Por essa razão, o MiniMental.com.br aponta para a fonte científica original e mantém separada a questão de <Link to="/formulario-e-direitos-de-uso" className=" text-[var(--primary)] hover:underline">formulário e direitos de uso do MMSE</Link>.
        </p>
      </section>

      <section aria-labelledby="tabela-brucki">
        <h2 id="tabela-brucki" className="text-2xl mb-5">Valores por escolaridade descritos em Brucki et al. (2003)</h2>
        <p className="text-[var(--text-muted)] mb-5">
          No estudo com {brucki.sampleSize} indivíduos saudáveis, os autores relataram <strong>medianas</strong> diferentes conforme a escolaridade. Esses valores são referências descritivas da amostra e não devem ser apresentados como pontos de corte diagnósticos universais.
        </p>
        <div className="overflow-x-auto border border-[var(--border)] rounded-2xl">
          <table className="w-full text-left">
            <thead className="bg-[var(--surface-soft)] text-[var(--primary)]">
              <tr><th className="p-4">Escolaridade</th><th className="p-4">Mediana no estudo</th></tr>
            </thead>
            <tbody className="divide-y divide-[var(--border)] text-[var(--text-muted)]">
              <tr><td className="p-4">Sem escolaridade formal</td><td className="p-4 font-bold">{medians.noFormalEducation}</td></tr>
              <tr><td className="p-4">1 a 4 anos</td><td className="p-4 font-bold">{medians.years1To4}</td></tr>
              <tr><td className="p-4">5 a 8 anos</td><td className="p-4 font-bold">{String(medians.years5To8).replace('.', ',')}</td></tr>
              <tr><td className="p-4">9 a 11 anos</td><td className="p-4 font-bold">{medians.years9To11}</td></tr>
              <tr><td className="p-4">12 anos ou mais</td><td className="p-4 font-bold">{medians.years12OrMore}</td></tr>
            </tbody>
          </table>
        </div>
        <p className="text-sm text-[var(--text-muted)] mt-4">
          Para entender como usar esses números sem confundir mediana com limiar de rastreio, veja <Link to="/escolaridade" className=" text-[var(--primary)] hover:underline">Mini Mental por escolaridade</Link>.
        </p>
      </section>

      <section aria-labelledby="para-imprimir">
        <h2 id="para-imprimir" className="text-2xl mb-5">Mini Mental para imprimir: o que verificar antes de usar</h2>
        <ol className="space-y-3 text-[var(--text-muted)] list-decimal pl-5">
          <li><strong>Origem:</strong> confirme de onde veio o material e qual versão está sendo usada.</li>
          <li><strong>Idioma:</strong> registre a versão em português brasileiro quando aplicável.</li>
          <li><strong>Integridade:</strong> não misture instruções, tarefas ou critérios de versões diferentes.</li>
          <li><strong>Direitos de uso:</strong> observe as condições vigentes para reprodução e utilização.</li>
          <li><strong>Referência:</strong> interprete o escore com uma referência coerente com população e escolaridade.</li>
        </ol>
      </section>

      <section aria-labelledby="alternativa-online">
        <h2 id="alternativa-online" className="text-2xl mb-5">Já tem a pontuação? Use a calculadora online</h2>
        <p className="text-[var(--text-muted)] mb-4">
          A calculadora deste site não substitui o formulário de aplicação: ela recebe apenas os pontos já obtidos em cada domínio e calcula o total no navegador.
        </p>
        <Link to="/#calculadora" className="inline-flex min-h-11 items-center text-[var(--primary)] hover:underline">Usar Mini Mental online →</Link>
      </section>

      <section aria-labelledby="aviso-clinico">
        <h2 id="aviso-clinico" className="text-2xl mb-5">Limite clínico</h2>
        <p className="text-[var(--text-muted)]">
          O MEEM é instrumento de rastreio. Um resultado isolado não confirma nem exclui diagnóstico e deve ser interpretado com história, funcionalidade, escolaridade, estado clínico e condições da aplicação.
        </p>
      </section>
    </ArticleLayout>
  );
}
